// Ticket releases are date-gated so pricing displays stay current without manual edits.
export function getCurrentRelease(releases, now = new Date()) {
  const active = releases.find(r => !r.soldOut && (!r.until || new Date(r.until) >= now));
  return active || releases[releases.length - 1];
}

function formatReleaseDate(until) {
  return new Date(until).toLocaleDateString('en-AU', { day: 'numeric', month: 'long' });
}

export function describeCurrentRelease(releases, now = new Date()) {
  const r = getCurrentRelease(releases, now);
  const dateStr = r.until ? ` available until ${formatReleaseDate(r.until)}` : '';
  return `${r.label} tickets ${r.price}${dateStr}`;
}

export function formatReleaseSchedule(releases, now = new Date()) {
  return releases
    .map(r => {
      if (r.soldOut) return `${r.label} – Sold Out`;
      if (r.until && new Date(r.until) < now) return `${r.label} – Closed`;
      return r.until ? `${r.label} ${r.price} (until ${formatReleaseDate(r.until)})` : `${r.label} ${r.price}`;
    })
    .join(' · ');
}

// An event counts as past once its endDate has fully elapsed, so the site
// doesn't need manual edits to move it out of "Upcoming Dates".
export function isEventPast(ev, now = new Date()) {
  if (!ev.endDate) return false;
  return new Date(`${ev.endDate}T23:59:59`) < now;
}

export const events = [
  {
    slug: 'nature-connection-sensory-play-jul-19',
    soldOut: true,
    endDate: '2026-07-19',
    emoji: '🌀',
    title: 'Nature Connection & Sensory Play',
    date: 'Sunday 19 July · 11am–1.30pm',
    duration: '2½ hours',
    location: 'Sydney, NSW',
    price: '$40',
    max: 20,
    img: '/backgrounds/tree-climb.jpg',
    desc: 'A playful, grounding experience designed to reconnect people with their senses, the landscape, and each other. Through gentle games, movement, and sensory invitations, we explore how to listen more closely to the natural world and to ourselves.',
    includesSummary: [
      'Guided sensory activities',
      'Nature-based games & movement',
      'Grounding meditation',
    ],
    includes: 'Guided sensory activities, nature-based games, and a grounding meditation.',
    bring: 'Comfortable clothes you can move in, water bottle, willingness to play.',
    ticketUrl: 'https://events.humanitix.com/nature-connection-and-sensory-play',
  },
  {
    slug: 'urban-foraging-aug-8',
    endDate: '2026-08-08',
    emoji: '🌿',
    tag: 'Subtle Animal Saturdays',
    title: 'Urban & Weed Foraging Walk',
    date: 'Saturday 8 August · 9.30am–12pm',
    duration: '2½ hours',
    location: 'Randwick Sustainability Hub, Randwick, Sydney',
    max: 20,
    img: '/backgrounds/urban-foraging-violets.jpg',
    desc: 'A reframing of the everyday landscape. This workshop invites you to see familiar streets, parks, and overlooked corners differently – as places of resilience, nourishment, and quiet abundance. Perfect for beginners and the quietly curious.',
    includesSummary: [
      'Guided urban foraging walk',
      'Recipes to take home',
      'Grounding meditation',
    ],
    includes: 'Expert guidance, recipes to take home, and a grounding meditation.',
    bring: 'Comfortable walking shoes, a small bag or basket, water bottle.',
    ticketUrl: 'https://events.humanitix.com/urban-and-weed-foraging-walk-08-08-26',
  },
  {
    slug: 'plant-printing-fabric-sep-12-26',
    endDate: '2026-09-12',
    emoji: '🍃',
    tag: 'Subtle Animal Saturdays',
    title: 'Plant Printing on Fabric',
    date: 'Saturday 12 September 2026 · 9.30am–12pm',
    duration: '2½ hours',
    location: 'Randwick Sustainability Hub, Randwick, Sydney',
    max: 20,
    img: '/backgrounds/plant-printing-fabric-closeup.png',
    desc: "Plant printing, or stamping, offers a slow, intentional practice that invites the natural world to be a direct creative collaborator. By gathering foliage to leave its tactile memory on fabric, this sensory process transforms textiles into living art that honors the delicate intelligence of the seasons.",
    includesSummary: [
      'Guided plant printing instruction',
      'All materials & plant specimens provided',
      'Your finished printed piece to take home',
    ],
    includes: 'Expert-led instruction, all materials and plant specimens, and your finished printed piece to take home.',
    bring: "Optionally bring a fabric or clothing item from home you'd like to print on – or use the textiles we provide.",
    ticketUrl: 'https://events.humanitix.com/plant-printing-on-natural-fabric-12-09-26',
  },
  {
    slug: 'weaving-lomandra-oct-10-26',
    endDate: '2026-10-10',
    emoji: '🧺',
    tag: 'Subtle Animal Saturdays',
    title: 'Weaving with Lomandra',
    date: 'Saturday 10 October 2026 · 9.30am–12pm',
    duration: '2½ hours',
    location: 'Randwick Sustainability Hub, Randwick, Sydney',
    max: 20,
    img: '/backgrounds/weaving-local-fibres.png',
    desc: "In this hands-on gathering, we honour lomandra (Lomandra longifolia) – one of Australia's most versatile and durable native plants, with deep significance in First Nations culture on Gadigal and Bidjigal Country. Learn to prepare your own fibre through cold-processing and splitting, then dive into traditional weaving techniques to create a small woven piece to take home.",
    includesSummary: [
      'Guided fibre preparation & weaving instruction',
      'All materials, including locally harvested lomandra',
      'Your finished woven piece to take home',
    ],
    includes: 'Expert-led instruction, all materials including locally and sustainably sourced lomandra, and your finished woven piece to take home.',
    bring: 'All materials are provided. Just bring yourself and a willingness to get your hands into the fibre.',
    ticketUrl: 'https://events.humanitix.com/weaving-with-lomandra-workshop-10-10-26',
  },
  {
    slug: 'wild-alignment',
    endDate: '2026-10-18',
    emoji: '🌲',
    tag: 'Immersion Weekend',
    title: 'Wild Alignment',
    date: 'Friday 16 – Sunday 18 October',
    duration: '3 days · 2 nights',
    location: 'Mountain Lagoon Hideaway, Bilpin NSW, Australia',
    img: '/backgrounds/wild-alignment-handstand.jpg',
    ticketUrl: 'https://events.humanitix.com/wild-alignment-nature-immersion-weekend',
  },
  {
    slug: 'botanical-skincare-nov-14-26',
    endDate: '2026-11-14',
    emoji: '🌱',
    tag: 'Subtle Animal Saturdays',
    title: 'Handcrafted Botanical Skincare Workshop',
    date: 'Saturday 14 November 2026 · 9.30am–12pm',
    duration: '2.5 hours',
    location: 'Randwick Sustainability Hub, Randwick, Sydney',
    max: 20,
    img: '/backgrounds/balm-making.jpg',
    desc: 'A slow, hands-on experience crafting natural skincare products from scratch, made with locally foraged plants and locally harvested beeswax. Learn the plant medicine behind every ingredient you use.',
    includesSummary: [
      'All materials & locally sourced ingredients',
      'Expert guidance on plant medicine',
      'Finished herbal balm to take home',
    ],
    includes: 'All materials, expert guidance, your finished herbal balm to take home.',
    bring: 'Just yourself – all materials are provided.',
    ticketUrl: 'https://events.humanitix.com/handcrafted-botanical-skincare-workshop',
  },
  {
    slug: 'native-incense-mar-13-27',
    endDate: '2027-03-13',
    emoji: '🕯️',
    tag: 'Subtle Animal Saturdays',
    title: 'Native Incense Cone Making Workshop',
    date: 'Saturday 13 March 2027 · 9.30am–12pm',
    duration: '2½ hours',
    location: 'Randwick Sustainability Hub, Randwick, Sydney',
    max: 20,
    img: '/backgrounds/incense-crafting.jpg',
    desc: 'In this hands-on workshop, we slow down to connect with the raw, therapeutic profiles of native Australian botanicals. You will learn the ancient art of incense crafting – pulverising creamy sandalwood, crisp eucalyptus, and bright lemon myrtle, before binding them with natural acacia gum. You will knead and shape your own custom-blended incense cones to take home for your daily rituals. The specific leaves, flowers, bark and resins used vary depending on seasonal availability.',
    includesSummary: [
      'Guided incense-crafting instruction',
      'All materials, including locally harvested botanicals',
      'Your hand-made incense cones to take home',
    ],
    includes: 'Expert-led instruction, all materials including locally and sustainably sourced botanicals, and your hand-made incense cones to take home.',
    bring: 'All materials are provided. Just bring yourself.',
    ticketUrl: 'https://events.humanitix.com/native-incense-cone-making-workshop-13-03-27',
  },
  {
    slug: 'botanical-skincare-for-pets-apr-10-27',
    endDate: '2027-04-10',
    emoji: '🐾',
    tag: 'Subtle Animal Saturdays',
    title: 'Handcrafted Botanical Skincare For Pets',
    date: 'Saturday 10 April 2027 · 9.30am–12pm',
    duration: '2.5 hours',
    location: 'Randwick Sustainability Hub, Randwick, Sydney',
    max: 20,
    img: '/backgrounds/pet-herbal-balm-july25.png',
    heroPosition: 'top',
    heroHeight: '600px',
    imgPosition: 'top',
    desc: 'A hands-on workshop in plant-based animal care. You\'ll make a balm for your dog from scratch, learning the plants, the process, and the reasoning behind it, and leave with a finished tin ready to use on dry paws, rough noses, and the small irritations of everyday life. We begin with the plants themselves: calendula for its gentle anti-inflammatory action, beeswax for protection and structure, shea and jojoba to soften and restore. You\'ll learn not just what goes in, but why, so you can adapt, experiment, and keep making long after the workshop ends.',
    includesSummary: [
      'Hands-on balm making from scratch',
      'Finished herbal balm tin to take home',
      'Recipes to recreate at home',
      'All materials, tea & snacks',
    ],
    includes: 'A grounded introduction to pet-safe plants and what they do for animal skin. Hands-on balm making – you measure, blend, pour, and label your own tin. One finished herbal balm to take home (for dry paws, rough noses, minor skin irritations, and everyday paw protection). Recipes to recreate and adapt at home. All materials and equipment provided. Tea, something to eat, good company.',
    bring: 'All materials and equipment are provided. No experience needed. Note: dogs cannot attend due to hot oils and waxes. The balm made in this workshop is a cosmetic product for external use on dogs – it is not a veterinary treatment. For skin conditions, open wounds, or health concerns, please consult your vet.',
    ticketUrl: 'https://events.humanitix.com/make-your-own-handcrafted-botanical-skincare-for-pets-10-04-27',
  },
  {
    slug: 'urban-foraging-may-8-27',
    endDate: '2027-05-08',
    emoji: '🌿',
    tag: 'Subtle Animal Saturdays',
    title: 'Urban & Weed Foraging Walk',
    date: 'Saturday 8 May 2027 · 9.30am–12pm',
    duration: '2½ hours',
    location: 'Randwick Sustainability Hub, Randwick, Sydney',
    max: 20,
    img: '/backgrounds/lilly-pilly-berries.jpg',
    desc: 'A reframing of the everyday landscape. This workshop invites you to see familiar streets, parks, and overlooked corners differently – as places of resilience, nourishment, and quiet abundance. Perfect for beginners and the quietly curious.',
    includesSummary: [
      'Guided urban foraging walk',
      'Recipes to take home',
      'Grounding meditation',
    ],
    includes: 'Expert guidance, recipes to take home, and a grounding meditation.',
    bring: 'Comfortable walking shoes, a small bag or basket, water bottle.',
    ticketUrl: 'https://events.humanitix.com/urban-and-weed-foraging-walk-08-08-26-08-05-27',
  },
  {
    slug: 'botanical-skincare-jun-5-27',
    endDate: '2027-06-05',
    emoji: '🌱',
    tag: 'Subtle Animal Saturdays',
    title: 'Handcrafted Botanical Skincare Workshop',
    date: 'Saturday 5 June 2027 · 9.30am–12pm',
    duration: '2.5 hours',
    location: 'Randwick Sustainability Hub, Randwick, Sydney',
    max: 20,
    img: '/backgrounds/botanical-skincare-jar.png',
    desc: 'A slow, hands-on experience crafting natural skincare products from scratch, made with locally foraged plants and locally harvested beeswax. Learn the plant medicine behind every ingredient you use.',
    includesSummary: [
      'All materials & locally sourced ingredients',
      'Expert guidance on plant medicine',
      'Finished herbal balm to take home',
    ],
    includes: 'All materials, expert guidance, your finished herbal balm to take home.',
    bring: 'Just yourself – all materials are provided.',
    ticketUrl: 'https://events.humanitix.com/handcrafted-botanical-skincare-workshop',
  },
];

export const featuredEvent = {
  slug: 'wild-alignment',
  startDate: '2026-10-16',
  endDate: '2026-10-18',
  emoji: '🌲',
  tag: 'Immersion Weekend',
  title: 'Wild Alignment',
  subtitle: 'With Ilana Hoffman & Lillian Donnell',
  date: 'Friday 16 – Sunday 18 October',
  duration: '3 days · 2 nights',
  releases: [
    { label: 'Early Bird', price: '$295', soldOut: true },
    { label: 'Second Release', price: '$340', until: '2026-08-21' },
    { label: 'Third Release', price: '$390', until: '2026-09-18' },
    { label: 'Final Release', price: '$450' },
  ],
  location: 'Mountain Lagoon Hideaway, Bilpin NSW, Australia',
  format: 'Camping',
  img: '/backgrounds/wild-alignment-banner.jpg',
  heroImg: '/backgrounds/wild-alignment-banner.jpg',
  images: ['/backgrounds/wild-alignment-plants.jpg'],
  facilitatorImage: '/backgrounds/wild-alignment-wildfoods.jpg',
  desc: 'Wild Alignment is a weekend immersion for those longing to slow down, awaken their senses, and find their way back to the living world. Over three days in the bush, we weave together movement, ancestral skills, and sensory awareness practices – creating space to feel alive, grounded, and held: by yourself, by each other, and by the land itself.',
  includesSummary: [
    'Movement, dance & fire circles',
    'Foraging, balm-making & plant printing on fabric',
    'Sensory awareness & bird language practices',
    'Camping accommodation on site',
    'Catered lunch & dinner, plus community',
  ],
  highlights: [
    { heading: 'Move & Be Moved', leader: null, body: "Our movement sessions draw on primal movement patterns, contact improvisation, and dance to awaken your body's innate intelligence. Evenings come alive around the fire with shared music and song." },
    { heading: 'Hands in the Earth', leader: null, body: 'Learn timeless bushcraft skills from foraging wild foods and making herbal balms, to plant printing on fabric and weaving. These are more than skills. They are invitations into reciprocity with the land.' },
    { heading: 'Open Your Senses', leader: null, body: "Through awareness practices, and the art of stalking your own curiosity, you'll learn to truly listen – to the forest, to the birds, to the subtler languages of the living world. These practices have the power to change the way you move through life, long after the weekend is over." },
  ],
  rhythm: "Each day holds its own rhythm: Stillness, shared meals, craft, movement, and fire. You're welcome to dip in and out of offerings as you feel called.",
  bring: "Camping gear and warm clothes, a cushion or chair to sit on, and a picnic rug each. A head torch, blindfold, water bottle, keep cup, and eating utensils. Natural insect repellent, a hat, bathers, and a towel. Food for breakfast and snacks (lunch and dinner are catered). A journal, something to write with, and a bag to carry it all. Tapestry needles for weaving. Musical instruments, songs, stories, and poems to share around the fire in the evenings. Any crafts already on the go. And extra firewood – always deeply appreciated.",
  closing: 'This weekend is for those who are ready to feel the earth under their feet, the fire at their back, and the wild aliveness that lives within them.',
  ticketUrl: 'https://events.humanitix.com/wild-alignment-nature-immersion-weekend',
  facilitators: [
    {
      name: 'Lillian Donnell',
      website: 'https://www.luminousflow.com.au/',
      bio: [
        'Lillian is a multi-modal movement coach, dancer, aerialist, musician, and a devoted student of the many ways a human body can feel truly alive.',
        'Her work draws on over 16 years of teaching across many disciplines, including aerial silks, bodyweight strength, mobility, Animal Flow, and Contact Improvisation Dance. Her passion is in helping people to reclaim the sense of profound joy that occurs when we inhabit our body, and develop the skills to move with more freedom, strength, and awareness.',
        'She runs workshops, retreats, and events that weave together movement, music, nature, and connection, and facilitates spaces – from live music and contact dance jams, to fire circles – where people can use their sense of embodied freedom to come into deeper self-expression and connection with others.',
      ],
    },
    {
      name: 'Ilana Hoffman',
      bio: [
        'Ilana has spent her life in close conversation with the living world – its creatures, its medicines, its older ways of knowing.',
        'Trained as a veterinarian, she has worked with animals across Australia and beyond, from wildlife medicine, rescue and conservation to community care in the field. Her work with animals is one thread in a wider web of life that has always called her: the plants underfoot, the birds overhead, the intelligence that moves through a forest when we finally go quiet enough to hear it.',
        'Ilana guides people into an embodied relationship with the land through ancestral skills, nature connection practices, foraging, and somatic inquiry. She is also a yoga and meditation teacher, dancer, freediver, and artist.',
        'What she creates is part science, part craft, part ceremony, and full of play.',
      ],
    },
  ],
};
